/**
 * 功能权限类型定义
 * @module feature-permission-types
 */

import type { PermissionV2Service } from './services/permission-v2-service';
import type { FeatureKey } from './types';

/**
 * 权限检查上下文
 */
export interface FeaturePermissionContext {
    /** 功能 ID */
    featureId: FeatureKey;
    /** 权限服务实例 */
    permissionService: PermissionV2Service;
    /** 用户套餐类型 */
    planType?: string;
    /** 用户 ID */
    userId?: string;
    /** 其他自定义数据 */
    metadata?: Record<string, any>;
}

/**
 * 权限检查结果
 */
export interface PermissionCheckResult {
    /** 是否有权限 */
    granted: boolean;
    /** 拒绝原因（当 granted = false 时） */
    reason?: string;
    /** 建议的操作（如 'upgrade_to_pro', 'contact_sales'） */
    suggestedAction?: string;
    /** 用于 UI 展示的消息 */
    message?: string;
}

/**
 * 功能权限策略接口
 */
export interface FeaturePermissionStrategy {
    /**
     * 获取该策略负责的功能 ID
     */
    readonly featureId: FeatureKey;

    /**
     * 检查权限
     * @param context 权限检查上下文
     * @returns 权限检查结果
     */
    checkPermission(context: FeaturePermissionContext): Promise<PermissionCheckResult>;

    /**
     * 记录使用量（可选）
     * @param context 权限检查上下文
     */
    recordUsage?(context: FeaturePermissionContext): Promise<void>;
}

export const FeaturePermissionStrategy = Symbol.for('FeaturePermissionStrategy');

/**
 * 功能权限注册表接口
 */
export interface FeaturePermissionRegistry {
    /**
     * 注册单个功能策略
     */
    register(featureId: FeatureKey, strategy: FeaturePermissionStrategy): void;

    /**
     * 批量注册
     */
    registerMany(strategies: Record<FeatureKey, FeaturePermissionStrategy>): void;

    /**
     * 获取策略
     */
    getStrategy(featureId: FeatureKey): FeaturePermissionStrategy | undefined;

    /**
     * 检查权限
     */
    checkPermission(
        featureId: FeatureKey,
        context: FeaturePermissionContext,
    ): Promise<PermissionCheckResult>;

    /**
     * 记录使用量
     */
    recordUsage(
        featureId: FeatureKey,
        context: FeaturePermissionContext,
    ): Promise<void>;
}

export const FeaturePermissionRegistry = Symbol.for('FeaturePermissionRegistry');
